/**
 * OrderArtworkTemplates
 *
 * Small panel rendered above the Artwork Upload area on the customer's
 * order-detail page. Resolves the correct templates from the SKUs used
 * on this order via GET /api/artwork-templates/for-order/{order_id}.
 *
 * Templates listed here are REFERENCE files only — downloading one does
 * NOT count as an uploaded artwork file. A template may contain
 * multiple files (e.g. digipak: front, tray, inner, spine).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ExternalLink, FileText, ChevronRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
    if (!n && n !== 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export default function OrderArtworkTemplates({ orderId }) {
    const [templates, setTemplates] = useState([]);
    const [matchMode, setMatchMode] = useState('sku');
    const [productType, setProductType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState({});
    // Whole-panel collapse — collapsed by default per user request.
    const [panelOpen, setPanelOpen] = useState(false);

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

    useEffect(() => {
        if (!orderId) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/artwork-templates/for-order/${orderId}`, { headers: authHeaders() });
                const tpls = data.templates || [];
                setTemplates(tpls);
                setMatchMode(data.match_mode || 'sku');
                setProductType(data.product_type || null);
                const initial = {};
                for (const t of tpls) initial[t.id] = (t.files || []).length <= 1;
                setExpanded(initial);
            } catch (e) {
                setError(e.response?.data?.detail || 'Failed to load templates');
            } finally {
                setLoading(false);
            }
        })();
    }, [orderId]);

    const handleDownloadFile = async (tplId, fileId) => {
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/${tplId}/files/${fileId}/download`, { headers: authHeaders() });
            window.open(data.download_url, '_blank');
        } catch (e) {
            toast.error('Download failed');
        }
    };

    const handlePreview = async (tpl) => {
        try {
            const { data } = await axios.get(`${API}/api/artwork-templates/${tpl.id}/preview`, { headers: authHeaders() });
            window.open(data.preview_url, '_blank');
        } catch (e) {
            toast.error('Preview not available');
        }
    };

    if (loading) return null;
    if (error) return null;

    const templateCount = templates.length;
    const fileCount = templates.reduce((n, t) => n + (t.files?.length || 0), 0);
    // Compact summary shown in the collapsed state.
    const summary = templateCount === 0
        ? 'No templates linked to this order'
        : `${templateCount} template${templateCount === 1 ? '' : 's'}` +
          (fileCount > 0 ? ` · ${fileCount} file${fileCount === 1 ? '' : 's'}` : '') +
          (matchMode === 'product_type_fallback' && productType ? ` · all ${productType}` : '');

    return (
        <div
            className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50/40"
            data-testid="order-templates-panel"
        >
            {/* Header row — always visible, click to expand/collapse. */}
            <button
                type="button"
                onClick={() => setPanelOpen(v => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-indigo-50/60 rounded-lg"
                aria-expanded={panelOpen}
                data-testid="order-templates-toggle"
            >
                <div className="flex items-center gap-2 min-w-0">
                    {panelOpen
                        ? <ChevronDown className="h-4 w-4 text-indigo-600 shrink-0" />
                        : <ChevronRight className="h-4 w-4 text-indigo-600 shrink-0" />}
                    <FileText className="h-4 w-4 text-indigo-600 shrink-0" />
                    <h4 className="font-semibold text-slate-900 text-sm shrink-0">Templates for this order</h4>
                    <span
                        className="text-xs text-slate-500 truncate"
                        data-testid="order-templates-summary"
                    >
                        · {summary}
                    </span>
                </div>
                <Link
                    to="/artwork-templates"
                    className="text-xs text-indigo-700 hover:underline flex items-center gap-1 shrink-0"
                    onClick={e => e.stopPropagation()}
                    data-testid="browse-all-templates-link"
                >
                    Browse all <ChevronRight className="h-3 w-3" />
                </Link>
            </button>

            {panelOpen && (
              <div className="px-4 pb-4 pt-0" data-testid="order-templates-body">
                {matchMode === 'product_type_fallback' && templates.length > 0 && (
                    <p className="text-[11px] text-slate-600 mb-3 italic" data-testid="templates-fallback-hint">
                        Showing all {productType ? `${productType} ` : ''}templates — the specific SKUs on your order will be confirmed once the quote is finalised.
                    </p>
                )}

            {templates.length === 0 ? (
                <p className="text-xs text-slate-600">
                    No specific template is linked to this order.{' '}
                    <Link to="/artwork-templates" className="text-indigo-700 hover:underline">
                        Browse the full template library
                    </Link>
                    .
                </p>
            ) : (
                <div className="space-y-2">
                    {templates.map(tpl => {
                        const files = tpl.files || [];
                        const isOpen = !!expanded[tpl.id];
                        return (
                            <div
                                key={tpl.id}
                                className="bg-white rounded p-2 border border-slate-200"
                                data-testid={`order-template-${tpl.id}`}
                            >
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setExpanded(s => ({ ...s, [tpl.id]: !s[tpl.id] }))}
                                        className="shrink-0 text-slate-400 hover:text-slate-700"
                                        title={isOpen ? 'Collapse' : 'Expand'}
                                        data-testid={`order-template-toggle-${tpl.id}`}
                                    >
                                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                    </button>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-slate-900 truncate" title={tpl.name}>
                                            {tpl.name}
                                        </div>
                                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                                            {tpl.category && <span>{tpl.category}</span>}
                                            {tpl.subcategory && <span>· {tpl.subcategory}</span>}
                                            <Badge variant="outline" className="text-[9px] uppercase h-4">
                                                {files.length} {files.length === 1 ? 'file' : 'files'}
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handlePreview(tpl)}
                                            title="Preview"
                                            data-testid={`order-template-preview-${tpl.id}`}
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                {isOpen && files.length > 0 && (
                                    <div className="mt-2 pl-7 space-y-1">
                                        {files.map(f => (
                                            <div
                                                key={f.id}
                                                className="flex items-center gap-2 text-xs bg-slate-50 rounded px-2 py-1.5"
                                                data-testid={`order-template-file-${f.id}`}
                                            >
                                                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate font-medium text-slate-800" title={f.filename}>
                                                        {f.label || f.filename}
                                                    </div>
                                                    {f.label && <div className="text-[10px] text-slate-400 truncate">{f.filename}</div>}
                                                </div>
                                                <span className="text-[10px] text-slate-400 shrink-0">{fmtBytes(f.size)}</span>
                                                <Button
                                                    size="sm"
                                                    className="h-7 px-2"
                                                    onClick={() => handleDownloadFile(tpl.id, f.id)}
                                                    data-testid={`order-template-download-${f.id}`}
                                                >
                                                    <Download className="h-3.5 w-3.5 mr-1" /> Download
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {isOpen && files.length === 0 && (
                                    <div className="mt-1 pl-7 text-[11px] text-slate-400 italic">No files uploaded yet.</div>
                                )}
                            </div>
                        );
                    })}
                    <p className="text-[11px] text-slate-500 italic mt-1">
                        Reference files only — downloading a template does not count as uploaded artwork.
                    </p>
                </div>
            )}
              </div>
            )}
        </div>
    );
}
